import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ValidationErrors, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from 'src/app/services/session-service';

type ResetState = 'invalidLink' | 'form' | 'success';

@Component({
  selector: 'pla-reset-password-page',
  templateUrl: './reset-password-page.component.html',
  styleUrls: ['./reset-password-page.component.css'],
})
export class ResetPasswordPageComponent implements OnInit {
  public state: ResetState = 'form';
  public loading = false;
  public errorMessage?: string;

  // Email + token come from the reset-link query string and are submitted
  // verbatim. We don't trim/normalise the token — it's URL-decoded already
  // by Angular's ActivatedRoute.
  public email = '';
  public token = '';

  public form = new FormGroup({
    newPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
    confirmPassword: new FormControl('', [Validators.required]),
  }, { validators: ResetPasswordPageComponent.matchPasswordsValidator });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    // Snapshot is sufficient — the reset link is opened fresh; we don't
    // expect token/email to mutate while the user is on the page.
    const params = this.route.snapshot.queryParamMap;
    const email = params.get('email');
    const token = params.get('token');
    if (!email || !token) {
      this.state = 'invalidLink';
      return;
    }
    this.email = email;
    this.token = token;
  }

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const newPassword = this.form.controls['newPassword'].value ?? '';
    this.loading = true;
    this.errorMessage = undefined;
    this.sessionService.resetPassword(this.email, this.token, newPassword).subscribe(
      (resp: any) => {
        this.loading = false;
        if (resp && resp.succeeded === true) {
          this.state = 'success';
          return;
        }
        // Identity reports failure inside a 200 response. Most common cause
        // is an expired or already-consumed token. Surface a single generic
        // message and give the user a way to request a new link.
        this.errorMessage = this.deriveErrorMessage(resp);
      },
      () => {
        this.loading = false;
        // 4xx/5xx — same generic copy. No account-existence leakage either
        // way because the prior /forgot-password call was constant.
        this.errorMessage = 'We could not reset your password. The link may have expired — please request a new one.';
      },
    );
  }

  private deriveErrorMessage(resp: any): string {
    const errors = resp?.errors as Array<{ description?: string }> | undefined;
    if (errors && errors.length > 0) {
      const first = errors[0]?.description;
      if (first && typeof first === 'string') return first;
    }
    return 'We could not reset your password. The link may have expired — please request a new one.';
  }

  public goToSignIn(): void {
    this.router.navigate(['/login']);
  }

  public goToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  // Custom group-level validator: confirm matches new.
  private static matchPasswordsValidator(group: AbstractControl): ValidationErrors | null {
    const a = group.get('newPassword')?.value;
    const b = group.get('confirmPassword')?.value;
    return a === b ? null : { passwordMismatch: true };
  }
}
